import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Grid,
  Button,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import {
  getAuthUpdate,
  updateAccountsBySwitch,
} from "../../api/EnvironmentApi/environmentApi";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../Common/SnackbarContext";
import { useLoader } from "../../context/LoaderContext";

import LocalStorageUtil from "../../utils/localStorage";

interface CustomModalProps {
  open: boolean;
  onClose: () => void;
  getBusinessUnitData: (id: any) => void;
  EnvironmentData: any[];
}

const SwitchEnvModal: React.FC<CustomModalProps> = ({
  open,
  onClose,
  EnvironmentData,
  getBusinessUnitData,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [hoveredImage, setHoveredImage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { showSnackbar } = useSnackbar();
  const { setLoading } = useLoader();

  const handleImageSelect = (env: any) => {
    setSelectedImage(env);
  };

  useEffect(() => {
    const contextName = LocalStorageUtil.getItem("initialContextName");
    if (contextName) {
      const matchedAccount = EnvironmentData.map(
        (account: any) => account
      ).find((account: any) => account?.contextName === contextName);
      if (matchedAccount) {
        setSelectedImage(matchedAccount);
      } else {
        setSelectedImage(null);
      }
    }
  }, [EnvironmentData]);

  const navigate = useNavigate();

  const handleChoose = async (env: any) => {
    setLoading(true);
    handleSwitchModal(env);
    const accId = env?.accountId;
    const payload = {
      AccountId: accId,
      Domainname: "",
      IsSuperAdmin: LocalStorageUtil.getItem("isSuperAdmin"),
      UserId: LocalStorageUtil.getItem("userId"),
      businessUnitId: env?.businessUnitId,
      contextName: env?.contextName,    
      UserName: LocalStorageUtil.getItem("userName"),
    };
    try {
      const response = await getAuthUpdate(payload);
      LocalStorageUtil.setItem("contextName", response?.dictionary.contextName);
      LocalStorageUtil.setItem(
        "folderRights",
        response?.dictionary.folderRights
      );
      LocalStorageUtil.setItem(
        "isSuperAdmin",
        response?.dictionary.isSuperAdmin
      );
      LocalStorageUtil.setItem("accountId", response?.dictionary.accountId);
      LocalStorageUtil.setItem(
        "businessUnitId",
        response?.dictionary.businessUnitId
      );
      LocalStorageUtil.setItem(
        "isAccountDSAEnabled",
        response?.dictionary.isAccountDSAEnabled
      );
      LocalStorageUtil.setItem(
        "isAccountDAOEnabled",
        response?.dictionary.isAccountDAOEnabled
      );
      LocalStorageUtil.setItem(
        "isAccountEdeliveryEnabled",
        response?.dictionary.isAccountEdeliveryEnabled
      );
      LocalStorageUtil.setItem(
        "authorizationData",
        JSON.stringify(response?.dictionary, null, 2)
      );
      getBusinessUnitData(response?.dictionary);
      navigate("/");
    } catch (error) {
      console.log("failed to load", error);
    }
    setLoading(false);
    onClose();
  };

  const handleSwitchModal = async (env: any) => {
    setLoading(true);
    const payload = {
      accountId: 0,
      businessUnitId:
        Number(env?.businessUnitId) ||
        Number(LocalStorageUtil.getItem("businessUnitId")),
      contextName: env?.contextName || LocalStorageUtil.getItem("contextName"),
      isSuperAdmin: LocalStorageUtil.getItem("isSuperAdmin"),
      userId: Number(env?.userId) || Number(LocalStorageUtil.getItem("userId")),
    };

    try {
      await updateAccountsBySwitch(payload);
      LocalStorageUtil.setItem("initialContextName", env?.contextName);
      setSelectedImage(env);
    } catch (error: any) {
      console.log("failed to load", error);
      if (error.response && error.response.status === 500) {
        showSnackbar(
          "You do not have the necessary access to this environment."
        );
      }
    }
    setLoading(false);
  };

  const filteredEnvironmentData = EnvironmentData?.filter((env) => {
    let accName = env?.accountName;
    return accName?.toLowerCase().includes(searchQuery.toLowerCase());
  }).sort((a, b) => a.accountName.localeCompare(b.accountName));

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="custom-modal-title"
      aria-describedby="custom-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "30%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          minHeight: 300,
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#006ab6",
            height: "45px",
            color: "white",
            px: 2,
            py: 1.5,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        >
          <Typography
            id="custom-modal-title"
            fontWeight="bold"
            fontSize="1.1rem"
          >
            Select Environment
          </Typography>
          <Tooltip title="Close" arrow>
            <IconButton onClick={onClose} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3, flexGrow: 1 }}>
          {/* Search Field */}
          <TextField
            placeholder="Search Accounts..."
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              mb: 3,
              "& .MuiInputBase-root": {
                height: "32px",
                fontSize: "0.85rem",
                borderRadius: "8px",
                padding: "0",
              },
              "& .MuiOutlinedInput-input": {
                padding: "8px 12px",
              },
              "& .MuiInputBase-input::placeholder": {
                fontSize: "0.85rem",
                color: "rgba(0, 0, 0, 0.6)",
              },
            }}
          />

          {/* Environment Logos */}
          <Grid container spacing={2}>
            {filteredEnvironmentData.map((env, index) => (
              <Tooltip
                key={env.contextName}
                title={env.contextName.replace("DocDistro_", "")}
                arrow
              >
                <Grid item xs={3}>
                  <Box
                    sx={{
                      position: "relative",
                      border:
                        selectedImage === env
                          ? "2px solid #006ab6"
                          : "1px solid #ccc",
                      borderRadius: "10px",
                      padding: "8px",
                      transition: "all 0.3s ease",
                      boxShadow:
                        selectedImage === env
                          ? "0px 4px 20px rgba(0, 106, 182, 0.3)"
                          : "none",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                    onClick={() => handleImageSelect(env)}
                    onMouseEnter={() => setHoveredImage(env)}
                    onMouseLeave={() => setHoveredImage(null)}
                  >
                    <StarIcon
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        fontSize: 20,
                        color: selectedImage === env ? "#005494" : "white",
                        stroke: "#005494",
                        strokeWidth: 2,
                        zIndex: 1,
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSwitchModal(env);
                      }}
                    />

                    <img
                      src={env.logoPath}
                      alt={`Environment ${index}`}
                      style={{
                        objectFit: "contain",
                        backgroundColor: "white",
                        borderRadius: "6px",
                        height: "6.25em",
                        margin: "0 auto",
                        width: "80%",
                        display: "block",
                      }}
                    />

                    {hoveredImage === env && (
                      <Button
                        variant="contained"
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          left: "50%",
                          transform: "translateX(-50%)",
                          backgroundColor: "#006ab6",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "0.65rem",
                          borderRadius: "20px",
                          px: 2,
                          py: 0.5,
                          boxShadow: 2,
                          "&:hover": {
                            backgroundColor: "#005494",
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChoose(env);                          
                        }}
                      >
                        Choose
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Tooltip>
            ))}
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};

export default SwitchEnvModal;
